import { createSelector } from 'reselect';
import { convertISODateToString } from '@beef/shop-common';

import { createMarkupAdjustableSelector, createMarkupSelector } from 'utils/markup';

import { getBodyData } from '../../selectors';
import { PREORDER_STATUS_INITIAL } from './constants';
import { PREORDER_FIELD_EMAIL } from '../../common/form/constants';

export const getPreorderData = (state) => getBodyData(state).preorderDetails || {};

export const getPreorderHasOnlinePaymentOffers = (state) =>
  getPreorderData(state).hasOnlinePaymentOffers || false;

export const getPreorderStartDate = (state) =>
  convertISODateToString(getPreorderData(state).startDate) || '';
export const getPreorderUntilDate = (state) =>
  convertISODateToString(getPreorderData(state).preorderUntilDate) || '';
export const getPreorderHeadline = (state) => getPreorderData(state).headline || '';
export const getPreorderStartTemplate = (state) =>
  getPreorderData(state).startTemplate || 'Старт продаж {start} года';
export const getPreorderRequestUrl = (state) => getPreorderData(state).preorderRequestUrl;
export const getPreorderPaymentOptionsUrl = (state) => getPreorderData(state).paymentOptionsUrl;
export const getPreorderPaymentDataUrl = (state) => getPreorderData(state).preorderPaymentDataUrl;
export const getPreorderPaymentActionUrl = (state) => getPreorderData(state).paymentActionUrl;
export const getPreorderPaymentData = (state) => getPreorderData(state).paymentData || null;
export const getPreorderPaymentDataAvailable = (state) => !!getPreorderData(state).paymentData;

const getPreorderAnnouncementTemplate = (state) =>
  getPreorderData(state).announcementTemplate || '';
export const getPreorderAnnouncement = createMarkupSelector(getPreorderAnnouncementTemplate, {
  startDate: getPreorderStartDate,
});

export const getPreorderStartMessage = createSelector(
  getPreorderStartDate,
  getPreorderStartTemplate,
  (date, template) => template.replace('{start}', date),
);

const getPreorderContentTemplate = (state) => getPreorderData(state).contentTemplate || '';
export const getPreorderContent = createMarkupSelector(getPreorderContentTemplate, {
  startDate: getPreorderStartDate,
  preorderUntilDate: getPreorderUntilDate,
});

export const getPreorderAgreementTemplate = (state) =>
  getPreorderData(state).agreementTemplate || '';
export const getPreorderAgreementAdjustableText = createMarkupAdjustableSelector(
  getPreorderAgreementTemplate,
);

export const getPreorderAgreementDataUrl = (state) => getPreorderData(state).agreementDataUrl;
export const getPreorderConditionsDataUrl = (state) => getPreorderData(state).conditionsDataUrl;

export const getPreorderOrderButtonText = (state) => getPreorderData(state).orderButtonText;

export const getPreorderPaymentAvailable = (state) =>
  getPreorderData(state).isPaymentAvailable || false;
const getPreorderPaymentTeaserRaw = (state) => getPreorderData(state).paymentTeaserTemplate;
export const getPreorderPaymentTeaser = createMarkupSelector(getPreorderPaymentTeaserRaw);
const getPreorderFullPaymentTeaserRaw = (state) => getPreorderData(state).fullPaymentTeaserTemplate;
export const getPreorderFullPaymentTeaser = createMarkupSelector(getPreorderFullPaymentTeaserRaw);
export const getPreorderPaymentTeaserButtonText = (state) =>
  getPreorderData(state).paymentTeaserButtonText || '';
export const getPreorderPaymentSubmitButtonText = (state) =>
  getPreorderData(state).paymentSubmitButtonText || '';

/** validation Errors for forms */
const getPreorderValidationErrors = (state) => getPreorderData(state).validationErrors || {};
export const getPreorderErrorFieldRequired = (state) =>
  getPreorderValidationErrors(state).fieldRequired;
export const getPreorderErrorPhoneInvalid = (state) =>
  getPreorderValidationErrors(state).phoneInvalid;
export const getPreorderErrorEmailInvalid = (state) =>
  getPreorderValidationErrors(state).emailInvalid;
export const getPreorderErrorNameInvalid = (state) =>
  getPreorderValidationErrors(state).nameInvalid;
export const getPreorderErrorAgreementUnchecked = (state) =>
  getPreorderValidationErrors(state).agreementUnchecked;
export const getPreorderErrorPaymentUnchecked = (state) =>
  getPreorderValidationErrors(state).paymentUnchecked;

/** local selectors */
export const getPreorderStatus = (state) =>
  getPreorderData(state).status || PREORDER_STATUS_INITIAL;
export const getPreorderOrderId = (state) => getPreorderData(state).orderId;
export const getPreorderSuccessMessageTemplate = (state) =>
  getPreorderData(state).successMessageTemplate;
export const getPreorderSuccessMessage = createMarkupSelector(getPreorderSuccessMessageTemplate, {
  orderId: getPreorderOrderId,
});
export const getPreorderNotificationMethod = (state) =>
  getPreorderData(state).notificationMethod || PREORDER_FIELD_EMAIL;
export const getPreorderPaymentFormOpen = (state) =>
  getPreorderData(state).paymentFormOpen || false;

export const getPreorderCallbackContact = (state) => getPreorderData(state).callbackContact || '';
export const getPreorderCallbackOrderId = (state) => getPreorderData(state).callbackOrderId || '';
export const getPreorderCallbackContactInfo = (state) => {
  const contact = getPreorderCallbackContact(state);
  if (contact) return `Детали отправлены на ${contact}. `;
  return '';
};
export const getPreorderHasOnlinePayment = (state) =>
  getPreorderData(state).hasOnlinePayment || false;

/* preorder in credit selectors */

export const getPreorderPaymentCreditText = (state) =>
  getPreorderData(state).referenceTemplate || '';
export const getPreorderHasInCreditOption = (state) =>
  getPreorderData(state).hasPreorderInCreditOption || false;

/** callback from payment service */
export const getPaymentCallbackErrorTemplate = (state) =>
  getPreorderData(state).callbackErrorTemplate;
export const getPaymentCallbackError = createMarkupSelector(getPaymentCallbackErrorTemplate, {
  orderNumber: getPreorderCallbackOrderId,
  contactInfo: getPreorderCallbackContactInfo,
});
export const getPaymentCallbackMessageTemplate = (state) =>
  getPreorderData(state).callbackSuccessTemplate;
export const getPaymentCallbackMessage = createMarkupSelector(getPaymentCallbackMessageTemplate, {
  orderNumber: getPreorderCallbackOrderId,
  contactInfo: getPreorderCallbackContactInfo,
});

export const getPreorderOnlyBasket = (state) => getPreorderData(state).onlyPreorderBasket || false;
